import { lazy, Suspense, type ReactElement } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { LanguageWrapper } from '@/components/LanguageRouter'
import { Layout } from '@/components/layout/Layout'
import { ChatBotProvider } from '@/contexts/ChatBotContext'
import { defaultLanguage, supportedLanguages } from '@/i18n'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

// Lazy-loaded pages
const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })))
const AboutPage = lazy(() => import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage })))
const DivisionsPage = lazy(() => import('@/pages/DivisionsPage').then((m) => ({ default: m.DivisionsPage })))

const ContactsPage = lazy(() => import('@/pages/ContactsPage').then((m) => ({ default: m.ContactsPage })))
const CertificationsPage = lazy(() => import('@/pages/CertificationsPage').then((m) => ({ default: m.CertificationsPage })))

const CareersPage = lazy(() => import('@/pages/CareersPage').then((m) => ({ default: m.CareersPage })))
const EnvironmentalLabelingPage = lazy(() => import('@/pages/EnvironmentalLabelingPage').then((m) => ({ default: m.EnvironmentalLabelingPage })))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })))
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage').then((m) => ({ default: m.CookiePolicyPage })))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })))


function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
    </div>
  )
}

function createLocalizedRoutes(routeKey: RouteKey, element: ReactElement) {
  return supportedLanguages.map((lang) => {
    const path = localizedRoutes[routeKey][lang]
    return path ? (
      <Route key={`${routeKey}-${lang}`} path={path} element={element} />
    ) : null
  }).filter(Boolean)
}

export default function App() {
  return (
    <ChatBotProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />
          <Route path="/:lang/*" element={<LanguageWrapper />}>
            <Route element={<Layout />}>
              <Route index element={<HomePage />} />
              {createLocalizedRoutes('contacts', <ContactsPage />)}
              {createLocalizedRoutes('privacy', <PrivacyPage />)}
              {createLocalizedRoutes('about', <AboutPage />)}
              {createLocalizedRoutes('divisions', <DivisionsPage />)}
              {createLocalizedRoutes('certifications', <CertificationsPage />)}        

              {createLocalizedRoutes('careers', <CareersPage />)}
              {createLocalizedRoutes('environmental', <EnvironmentalLabelingPage />)}
              {createLocalizedRoutes('cookiePolicy', <CookiePolicyPage />)}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </ChatBotProvider>
  )
}
